import apiConfig from '../config/api';

// ---- Wire types (mirror strategy_systematics::config, snake_case enums) ----

export type SignalFunc = 'mptile_csum' | 'mean_rev2575_csum';
export type AnnVolFunc = 'mstd120_annualized';
export type SelRule = 'unrestricted' | 'last_only';
export type SliceType = 'full_sample' | 'rolling_window' | 'expanding_window';
export type MixingObjective = 'sharpe_ratio' | 'crr';

export interface SignalSpec {
  name: string;
  base_ts: string;
  func: SignalFunc;
  params: number[];
  tc_opt: boolean;
  sel_rule: SelRule;
  wt_cap: number | null;
}

export interface FitSpec {
  slice_type: SliceType;
  fit_window: string;
  oos_window: string;
  oos_start_anchor: string | null;
}

export interface StrategyConfig {
  strategy_name: string;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  pnl_ts: string;
  ann_vol_func: AnnVolFunc;
  tc_lin_int: number;
  tc_lin_slope: number;
  tc_quad: number;
  sr_improv_thresh: number;
  tca_vec: number[];
  ra_vec: number[];
  vol_target: number;
  filter_by_vol: boolean;
  upper_risk_limit: number;
  upper_position_limit: number | null;
  scale_signals_to_asset_vol: boolean;
  vol_trigger_signal: boolean;
  vol_trigger_level: number;
  corr_shrinkage: number;
  mixing_objective: MixingObjective;
  non_mv_run: boolean;
  signals: SignalSpec[];
  aum: number;
}

export interface PerfRow {
  label: string;
  begin: string;
  end: string;
  sharpe_ratio: number | null;
  ann_vol: number | null;
  max_dd: number | null;
  skew: number | null;
  kurtosis: number | null;
  cr_ratio_5p: number | null;
}

export interface SliceLoadings {
  fit_start: string;
  fit_end: string;
  oos_start: string;
  oos_end: string;
  loadings: Array<[string, number]>;
}

// Full-sample derived series for Excel export. Carries its own `dates` axis (whole run
// range), distinct from the OOS-stitched `BacktestResponse.dates`. Per-signal matrices are
// column-per-name, aligned to `names`.
export interface BacktestSeries {
  dates: string[];
  pnl: Array<number | null>;
  ann_vol: Array<number | null>;
  ann_vol_lag1: Array<number | null>;
  voladj_pnl: Array<number | null>;
  names: string[];
  raw_signal: Array<Array<number | null>>;
  signal_lag: Array<Array<number | null>>;
  signal_pnl: Array<Array<number | null>>;
  signal_pnl_vol: Array<Array<number | null>>;
  signal_scaler: Array<Array<number | null>>;
  signal_scaler_lag: Array<Array<number | null>>;
  normalized_signal: Array<Array<number | null>>;
  normalized_signal_lag: Array<Array<number | null>>;
  normalized_signal_pnl: Array<Array<number | null>>;
}

export interface BacktestResponse {
  dates: string[];
  pnl: number[];
  cum_pnl: number[];
  pos: Array<number | null>;
  // OOS-stitched traded combined signal (C_t), aligned with `dates`.
  combined_signal: Array<number | null>;
  perf: PerfRow[];
  slices: SliceLoadings[];
  series: BacktestSeries;
}

// ---- Human-friendly option labels for dropdowns ----

export const SIGNAL_FUNC_OPTIONS: Array<{ value: SignalFunc; label: string }> = [
  { value: 'mptile_csum', label: 'Trend — moving percentile of cumsum (mptile_csum)' },
  { value: 'mean_rev2575_csum', label: 'Mean reversion — 25/75 bands (mean_rev_25_75_csum)' },
];

export const ANN_VOL_FUNC_OPTIONS: Array<{ value: AnnVolFunc; label: string }> = [
  { value: 'mstd120_annualized', label: '120d rolling std × √252 (mstd_120_annualized)' },
];

export const SEL_RULE_OPTIONS: Array<{ value: SelRule; label: string }> = [
  { value: 'unrestricted', label: 'Unrestricted' },
  { value: 'last_only', label: 'Last only' },
];

export const SLICE_TYPE_OPTIONS: Array<{ value: SliceType; label: string }> = [
  { value: 'rolling_window', label: 'Rolling window' },
  { value: 'expanding_window', label: 'Expanding window' },
  { value: 'full_sample', label: 'Full sample' },
];

export const MIXING_OBJECTIVE_OPTIONS: Array<{ value: MixingObjective; label: string }> = [
  { value: 'sharpe_ratio', label: 'Sharpe ratio' },
  { value: 'crr', label: 'CR ratio' },
];

// ---- Form draft: signals are edited partially filled, so their enums allow '' ----

export interface SignalDraft {
  name: string;
  base_ts: string;
  func: SignalFunc | '';
  params: number[];
  tc_opt: boolean;
  sel_rule: SelRule | '';
  wt_cap: number | null;
}

export interface GridRange {
  start: number;
  end: number;
  step: number;
}

export type StrategyDraft = Omit<StrategyConfig, 'signals' | 'tca_vec' | 'ra_vec'> & {
  signals: SignalDraft[];
  tca_range: GridRange;
  ra_range: GridRange;
};

const MAX_GRID_VALUES = 200;

const round10 = (v: number): number => Math.round(v * 1e10) / 1e10;

const decimalsOf = (v: number): number => (String(v).split('.')[1] ?? '').length;

/// Null when the range is usable, otherwise the reason it is not.
export const gridRangeError = ({ start, end, step }: GridRange): string | null => {
  if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(step)) return 'start, end and step must be numbers';
  if (step <= 0) return 'step must be greater than 0';
  if (end < start) return 'end must be greater than or equal to start';
  if (Math.floor((end - start) / step + 1e-9) + 1 > MAX_GRID_VALUES) return `range expands to more than ${MAX_GRID_VALUES} values`;
  return null;
};

/// Expands start/end/step into the inclusive grid. Caller must check `gridRangeError` first.
export const expandGridRange = ({ start, end, step }: GridRange): number[] => {
  const count = Math.floor((end - start) / step + 1e-9) + 1;
  return Array.from({ length: count }, (_, i) => round10(start + i * step));
};

/// Renders the expanded grid the way Python would print the list.
export const gridRangePreview = (range: GridRange): string => {
  const digits = Math.min(6, Math.max(1, decimalsOf(range.start), decimalsOf(range.step)));
  return `[${expandGridRange(range).map((v) => v.toFixed(digits)).join(', ')}]`;
};

export const emptySignal = (): SignalDraft => ({
  name: '',
  base_ts: '',
  func: '',
  params: [],
  tc_opt: false,
  sel_rule: '',
  wt_cap: null,
});

// Throws on any partially-filled signal; the caller surfaces the message.
export const toStrategyConfig = (draft: StrategyDraft): StrategyConfig => {
  const { tca_range, ra_range, ...rest } = draft;
  const tcaErr = gridRangeError(tca_range);
  if (tcaErr) throw new Error(`TC aversion grid: ${tcaErr}`);
  const raErr = gridRangeError(ra_range);
  if (raErr) throw new Error(`Risk aversion grid: ${raErr}`);

  return {
    ...rest,
    tca_vec: expandGridRange(tca_range),
    ra_vec: expandGridRange(ra_range),
    signals: draft.signals.map((s, i) => {
      const label = s.name.trim() || `Signal ${i + 1}`;
      if (!s.name.trim()) throw new Error(`${label}: name is required`);
      if (!s.base_ts.trim()) throw new Error(`${label}: base ticker is required`);
      if (s.func === '') throw new Error(`${label}: transform is required`);
      if (s.sel_rule === '') throw new Error(`${label}: selection rule is required`);
      if (s.params.length === 0) throw new Error(`${label}: at least one window param is required`);
      return { ...s, name: s.name.trim(), base_ts: s.base_ts.trim(), func: s.func, sel_rule: s.sel_rule };
    }),
  };
};

// ---- Default config (mirrors config::cl_1v2 + scratchpad_fit_specs; signals start empty) ----

export const DEFAULT_STRATEGY_DRAFT: StrategyDraft = {
  strategy_name: 'CL_1v2_scratchpad',
  start_date: '2012-01-01',
  end_date: '2025-01-01',
  pnl_ts: 'cl_cl_1_2_ME1_N1_pnl',
  ann_vol_func: 'mstd120_annualized',
  tc_lin_int: 0.0,
  tc_lin_slope: 0.0,
  tc_quad: 0.0,
  sr_improv_thresh: 0.1,
  tca_range: { start: 0.0, end: 0.2, step: 0.1 },
  ra_range: { start: 0.8, end: 1.1, step: 0.1 },
  vol_target: 0.01,
  filter_by_vol: false,
  upper_risk_limit: 0.015,
  upper_position_limit: null,
  scale_signals_to_asset_vol: false,
  vol_trigger_signal: false,
  vol_trigger_level: 0.0,
  corr_shrinkage: 0.25,
  mixing_objective: 'sharpe_ratio',
  non_mv_run: false,
  aum: 1_000_000.0,
  signals: [],
};

export const DEFAULT_FIT_SPEC: FitSpec = {
  slice_type: 'rolling_window',
  fit_window: '5y',
  oos_window: '1y',
  oos_start_anchor: null,
};

// ---- API call: hits the Rust gateway (not the chatbot base) ----

export const runStrategySystematics = async (
  config: StrategyConfig,
  fitSpec: FitSpec,
  signal?: AbortSignal,
): Promise<BacktestResponse> => {
  const url = `${apiConfig.rustApiUrl}/strategy-systematics/run`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ config, fit_spec: fitSpec }),
    signal,
  });

  const text = await response.text();
  if (!response.ok) {
    let message = `Backtest request failed with status ${response.status}`;
    if (text) {
      try {
        const parsed = JSON.parse(text) as { message?: string; error?: string };
        message = parsed.message ?? parsed.error ?? text;
      } catch {
        message = text;
      }
    }
    throw new Error(message);
  }

  return JSON.parse(text) as BacktestResponse;
};
